// ---------------------------------------------------------------------------
// DSS-21: Hover animation picker — grid of 20 presets with hover preview
// ---------------------------------------------------------------------------
import { useState, useRef, useCallback, useId } from "react";
import type { DssHoverPresetId } from "@ease/page-builder";
import { Pointer, X } from "lucide-react";
import { HOVER_ANIMATIONS, findHoverPreset, type HoverAnimationPreset } from "./hover-animations";

// ── Constants ──
const GRID_COLS = 5;

// ── Props ──
interface HoverAnimationPickerProps {
  readonly value: DssHoverPresetId | null;
  readonly onChange: (animationId: DssHoverPresetId | null) => void;
}

// ── Component ──
export default function HoverAnimationPicker({ value, onChange }: HoverAnimationPickerProps) {
  const uid = useId();
  const selectedPreset = value ? findHoverPreset(value) : null;

  const handleSelect = useCallback(
    (preset: HoverAnimationPreset) => {
      onChange(preset.id);
    },
    [onChange],
  );

  const handleClear = useCallback(() => {
    onChange(null);
  }, [onChange]);

  return (
    <div className="flex flex-col gap-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs font-semibold text-[var(--color-foreground)]">
          <Pointer className="h-3.5 w-3.5" />
          Hover Animation
        </div>
        {value && (
          <button
            type="button"
            onClick={handleClear}
            className="flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] text-[var(--color-muted-foreground)] hover:bg-[var(--color-muted)] hover:text-[var(--color-foreground)]"
          >
            <X className="h-3 w-3" />
            None
          </button>
        )}
      </div>

      {/* Grid */}
      <div
        className="grid gap-1.5"
        style={{ gridTemplateColumns: `repeat(${GRID_COLS}, 1fr)` }}
      >
        {HOVER_ANIMATIONS.map((preset) => (
          <HoverThumbnail
            key={preset.id}
            preset={preset}
            isSelected={value === preset.id}
            onSelect={handleSelect}
          />
        ))}
      </div>

      {/* Selected info */}
      {selectedPreset && (
        <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-muted)]/40 p-3">
          <div className="flex items-center gap-3">
            <HoverPreviewBox preset={selectedPreset} uid={uid} />
            <span className="text-xs font-medium text-[var(--color-foreground)]">
              {selectedPreset.name}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Thumbnail ──
interface ThumbnailProps {
  readonly preset: HoverAnimationPreset;
  readonly isSelected: boolean;
  readonly onSelect: (preset: HoverAnimationPreset) => void;
}

function HoverThumbnail({ preset, isSelected, onSelect }: ThumbnailProps) {
  const [isHovered, setIsHovered] = useState(false);
  const scopeId = `dss-hover-${preset.id}`;

  return (
    <button
      type="button"
      onClick={() => onSelect(preset)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      title={preset.name}
      className={[
        "flex flex-col items-center gap-1 rounded-md border p-1.5 text-center",
        isSelected
          ? "border-[var(--color-primary)] bg-[var(--color-primary)]/10"
          : "border-[var(--color-border)] hover:border-[var(--color-primary)]/50",
      ].join(" ")}
    >
      {isHovered && (
        <style>{scopedHoverCss(scopeId, preset.css)}</style>
      )}
      <div
        className={`${scopeId} h-5 w-5 rounded-sm bg-[var(--color-primary)]`}
        style={{ opacity: isHovered ? undefined : 0.4 }}
      />
      <span className="w-full truncate text-[8px] leading-tight text-[var(--color-muted-foreground)]">
        {preset.name}
      </span>
    </button>
  );
}

// ── Live preview box ──
function HoverPreviewBox({
  preset,
  uid,
}: {
  readonly preset: HoverAnimationPreset;
  readonly uid: string;
}) {
  const scopeId = `dss-hover-preview-${uid.replace(/:/g, "")}`;

  return (
    <div className="relative flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-md border border-[var(--color-border)] bg-[var(--color-card)]">
      <style>{scopedHoverCss(scopeId, preset.css)}</style>
      <div className={`${scopeId} h-5 w-5 rounded-sm bg-[var(--color-primary)]`} />
    </div>
  );
}

/**
 * Scope hover CSS to a specific class name.
 * Replaces `:hover` with `.className:hover` and bare `{` with `.className {`.
 */
function scopedHoverCss(className: string, css: string): string {
  return css
    .replace(/:hover/g, `.${className}:hover`)
    .replace(/::after/g, `.${className}::after`)
    .replace(/^{/gm, `.${className} {`);
}
